import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import maplibregl, { MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl';
import { AlertContainer, Button, ButtonVariant } from 'ui/atoms';

import CountriesSidebar from './CountriesSidebar';
import { CountryInfoCard } from './CountryView/components/CountryInfoCard';
import { PostcodeSidebar } from './CountryView/components/PostcodeSidebar';
import { PostcodeTooltipCard } from './CountryView/components/PostcodeTooltipCard';
import { RegionLoader } from './CountryView/components/RegionLoader';
import { RegionTooltipCard } from './CountryView/components/RegionTooltipCard';
import { RegionsSidebar } from './CountryView/components/RegionsSidebar';
import { SimpleRegionTooltip } from './CountryView/components/SimpleRegionTooltip';
import { useMapInteractions } from './CountryView/hooks/useMapInteractions';
import { PostcodeMarker } from './CountryView/types';
import { featureBboxCenter } from './CountryView/utils/featureBboxCenter';
import {
  C,
  COUNTRY_CENTERS,
  COUNTRY_NAMES,
  Incident,
  IncidentsData,
  ThresholdConfig,
  getColor,
  getSeverity,
  withBase,
} from './MapSharedData';
import { useUnifiedAggregation } from './hooks/useUnifiedAggregation';
import { useUnifiedCountryData } from './hooks/useUnifiedCountryData';
import { useUnifiedCountryOutline } from './hooks/useUnifiedCountryOutline';
import { useUnifiedDrillIn } from './hooks/useUnifiedDrillIn';
import { useUnifiedDrillOut } from './hooks/useUnifiedDrillOut';
import { useUnifiedMap } from './hooks/useUnifiedMap';
import { useUnifiedNavigation } from './hooks/useUnifiedNavigation';
import { useUnifiedPolygonLoader } from './hooks/useUnifiedPolygonLoader';
import { useUnifiedPostcodeLayers } from './hooks/useUnifiedPostcodeLayers';
import { useUnifiedRegionLayers } from './hooks/useUnifiedRegionLayers';
import { ISO3_TO_ISO2 } from './iso3ToIso2';

// Single MapLibre instance backing both the world overview and country
// drill-downs. Replaces the legacy WorldView + CountryView pair: layers
// from both live on the same map and crossfade via fill-opacity-transition,
// so the unmount/remount white flash is gone and drill-in/out animate on
// the GPU.

const WORLD_GEOJSON_URL = '/geojson/world-countries.geojson';
const ISO3_PROP = 'shapeGroup';

const SOURCE_ID = 'countries';
const FILL_LAYER = 'countries-fill';
const HOVER_LAYER = 'countries-hover';
const LABEL_LAYER = 'countries-label';

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

interface UnifiedMapProps {
  data: IncidentsData;
  /** Per-country MAX postcode count — drives world fill color. */
  countryTotals: Record<string, number>;
  /** Per-country SUM of postcode counts — sidebar/tooltip totals. */
  countrySums: Record<string, number>;
  cfg: ThresholdConfig;
  activeCountry: string | null;
  activeRegion: string | null;
  focusedPostcode: string | null;
  onSelectCountry: (code: string, view?: { center: [number, number]; zoom: number }) => void;
  onActiveRegionChange: (region: string | null) => void;
  onFocusedPostcodeChange: (pc: string | null) => void;
  onBack: (viewport?: { center: [number, number]; zoom: number }) => void;
}

/**
 * Imperative handle exposed to IncidentMap. Mirrors the legacy
 * { WorldViewHandle, CountryViewHandle } pair on a single ref:
 *   - getViewport: snapshot center+zoom for handoffs (breadcrumb navigation)
 *   - fitCountry:  zoom the world camera to a country's bounds without
 *                  drilling in — used for countries that have incidents
 *                  but no drill-down config in `countries`.
 */
export interface UnifiedMapHandle {
  getViewport: () => { center: [number, number]; zoom: number } | null;
  fitCountry: (countryCode: string) => void;
}

interface WorldTooltip {
  countryCode: string;
  countryName: string;
  count: number;
  severityCount: number;
  x: number;
  y: number;
}

const UnifiedMap = forwardRef<UnifiedMapHandle, UnifiedMapProps>(function UnifiedMap(
  {
    data,
    countryTotals,
    countrySums,
    cfg,
    activeCountry,
    activeRegion,
    focusedPostcode,
    onSelectCountry,
    onActiveRegionChange,
    onFocusedPostcodeChange,
    onBack,
  },
  ref,
) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const { mapRef, ready } = useUnifiedMap({ containerRef: mapContainer });

  const [worldTooltip, setWorldTooltip] = useState<WorldTooltip | null>(null);
  const [countryLoaderPos, setCountryLoaderPos] = useState<[number, number] | null>(null);
  const [regionLoaderPos, setRegionLoaderPos] = useState<[number, number] | null>(null);

  // Cached world FeatureCollection — populated after the async fetch below,
  // referenced by fitCountry() so we can resolve a country's bounds on demand
  // without re-querying MapLibre.
  const worldGeoJsonRef = useRef<GeoJSON.FeatureCollection | null>(null);

  // ── World fill color expression ────────────────────────────────────────
  const buildExpr = useCallback((): maplibregl.ExpressionSpecification => {
    const pairs: (string | maplibregl.ExpressionSpecification)[] = [];
    for (const [iso3, a2] of Object.entries(ISO3_TO_ISO2)) {
      const count = countryTotals[a2] ?? 0;
      if (count > 0) {
        pairs.push(iso3, getColor(count, cfg));
      }
    }
    if (!pairs.length) return 'rgba(0,0,0,0)' as any;
    return ['match', ['get', ISO3_PROP], ...pairs, 'rgba(0,0,0,0)'] as any;
  }, [countryTotals, cfg]);

  // ── 1. Add world source + layers synchronously once the map is ready ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getSource(SOURCE_ID)) return;

    map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_FC, generateId: true });

    map.addLayer({
      id: FILL_LAYER,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': buildExpr(),
        // Start invisible — the world geojson load effect below promotes
        // this to the zoom-interpolation expression once setData has run,
        // which under fill-opacity-transition produces a smooth fade-in
        // instead of the hard snap that came with empty-source → populated
        // mounting.
        'fill-opacity': 0,
        'fill-opacity-transition': { duration: 300 },
        'fill-outline-color': 'rgba(255, 255, 255, 0.4)',
      },
    });

    map.addLayer({
      id: HOVER_LAYER,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': C.primary,
        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.5, 0],
      },
    });

    map.addLayer({
      id: LABEL_LAYER,
      type: 'symbol',
      source: SOURCE_ID,
      minzoom: 2,
      filter: ['!=', ['get', 'name'], ''],
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 2, 10, 4, 13, 6, 15],
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-padding': 2,
        'symbol-placement': 'point',
      },
      paint: {
        'text-color': C.textPrimary,
        'text-halo-color': 'rgba(255,255,255,0.95)',
        'text-halo-width': 1.6,
        'text-halo-blur': 0.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 2, 0, 2.5, 1],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, ready]);

  // ── 2. Load world geojson + setData ────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(withBase(WORLD_GEOJSON_URL));
        const geojson = (await res.json()) as GeoJSON.FeatureCollection;
        if (cancelled) return;

        for (const f of geojson.features) {
          const iso3 = String(f.properties?.[ISO3_PROP] ?? '');
          const a2 = ISO3_TO_ISO2[iso3];
          if (!f.properties) f.properties = {};
          f.properties.a2 = a2 ?? '';
          f.properties.name = a2 ? COUNTRY_NAMES[a2] ?? a2 : '';
        }

        worldGeoJsonRef.current = geojson;
        const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        if (source) source.setData(geojson);

        // Trigger the initial fade-in. The layer was created with
        // fill-opacity: 0 + a 800 ms transition; raising it to the
        // zoom-interpolation expression here makes MapLibre tween from 0
        // to the per-pixel expression value smoothly instead of the
        // abrupt pop the user would otherwise see at setData time.
        if (map.getLayer(FILL_LAYER)) {
          map.setPaintProperty(FILL_LAYER, 'fill-opacity', [
            'interpolate',
            ['linear'],
            ['zoom'],
            2,
            0.75,
            5,
            0.3,
          ]);
        }
      } catch (err) {
        console.error('UnifiedMap world geojson load error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapRef, ready]);

  // ── 3. Update world fill color when threshold / data changes ───────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getLayer(FILL_LAYER)) return;
    map.setPaintProperty(FILL_LAYER, 'fill-color', buildExpr());
  }, [mapRef, ready, buildExpr]);

  // ── 4. World hover / click — disabled while drilled into a country ─────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (!map.getLayer(FILL_LAYER)) return;

    let hoveredId: string | number | undefined;

    const clearHover = () => {
      if (hoveredId === undefined) return;
      try {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false });
      } catch {
        // Map / source torn down — nothing to reset.
      }
      hoveredId = undefined;
    };

    const onMove = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (activeCountry) return;
      if (!e.features?.length) return;
      const feature = e.features[0];
      const iso3 = String(feature.properties?.[ISO3_PROP] ?? '');
      const a2 = ISO3_TO_ISO2[iso3];

      // Moving onto a country we don't recognise OR one with no incidents:
      // mouseleave won't fire (the cursor is still on FILL_LAYER, just on a
      // different feature), so clear the lingering hover + tooltip from the
      // previous feature explicitly. Without this, the tooltip and the
      // green overlay stick to the last "live" country.
      if (!a2 || (countryTotals[a2] ?? 0) === 0) {
        map.getCanvas().style.cursor = '';
        clearHover();
        setWorldTooltip(null);
        return;
      }
      const count = countryTotals[a2];
      map.getCanvas().style.cursor = 'pointer';

      if (hoveredId !== undefined) {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false });
      }
      hoveredId = feature.id;
      if (hoveredId !== undefined) {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: true });
      }

      setWorldTooltip({
        countryCode: a2,
        countryName: COUNTRY_NAMES[a2] ?? a2,
        count: countrySums?.[a2] ?? count,
        severityCount: count,
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
      });
    };

    const onLeave = () => {
      map.getCanvas().style.cursor = '';
      clearHover();
      setWorldTooltip(null);
    };

    const onClick = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
      if (activeCountry) return;
      if (!e.features?.length) return;
      const feature = e.features[0];
      const iso3 = String(feature.properties?.[ISO3_PROP] ?? '');
      const a2 = ISO3_TO_ISO2[iso3];
      if (!a2 || (countryTotals[a2] ?? 0) === 0) return;

      // Reset hover state immediately — drill-in starts a flyTo that moves
      // the map out from under the cursor, so mouseleave doesn't always
      // fire and the green hover overlay would otherwise stay stuck on
      // the clicked country through the crossfade.
      clearHover();
      map.getCanvas().style.cursor = '';
      setWorldTooltip(null);

      const c = map.getCenter();
      onSelectCountry(a2, { center: [c.lng, c.lat], zoom: map.getZoom() });
    };

    map.on('mousemove', FILL_LAYER, onMove);
    map.on('mouseleave', FILL_LAYER, onLeave);
    map.on('click', FILL_LAYER, onClick);

    return () => {
      // Drop any lingering hover feature-state. Without this, a hover that
      // was active when the effect re-binds (e.g. activeCountry changed)
      // would survive on the next world view as a stuck green tint.
      clearHover();
      map.off('mousemove', FILL_LAYER, onMove);
      map.off('mouseleave', FILL_LAYER, onLeave);
      map.off('click', FILL_LAYER, onClick);
    };
  }, [mapRef, ready, activeCountry, countryTotals, countrySums, onSelectCountry]);

  // ── 5. Country-level data + layers (steps 2–3 infrastructure) ──────────
  const {
    geojson: countryGeoJson,
    postcodes: countryPostcodes,
    config: countryConfig,
    error: countryDataError,
  } = useUnifiedCountryData(activeCountry);
  const regionNameProperty = countryConfig?.regionNameProperty ?? 'name';
  const postcodeProperty = countryConfig?.postcodeProperty ?? 'postal_code';
  const postcodeGeoJsonUrl = countryConfig?.postcodeGeoJsonUrl;

  // Per-country incidents flattened to Incident[] (same shape the legacy
  // CountryView received from IncidentMap).
  const countryIncidents = useMemo<Incident[]>(() => {
    if (!activeCountry) return [];
    const entry = data[activeCountry];
    if (!entry) return [];
    return entry.postcodes.map((p) => ({
      country: activeCountry,
      postcode: p.code,
      count: p.count,
    }));
  }, [data, activeCountry]);

  const {
    aggregated,
    allMarkers,
    unmatchedCount,
    country: aggregatedCountry,
  } = useUnifiedAggregation({
    activeCountry,
    incidents: countryIncidents,
    postcodes: countryPostcodes,
    regionsGeoJson: countryGeoJson,
    regionNameProperty,
  });

  // Lazy postcode-polygon loader. Cache is per-country (reset on swap).
  const {
    polygonsByRegion,
    loading: polygonsLoading,
    error: polygonsError,
    resolveUrl,
    loadForRegion,
    reset: resetPolygons,
  } = useUnifiedPolygonLoader({ postcodeGeoJsonUrl, activeCountry });

  // Trigger polygon load when a region opens; reset loader status when it
  // closes. Also clears any focused postcode when the region closes — the
  // postcode lives inside the region.
  useEffect(() => {
    if (activeRegion && countryGeoJson) {
      const regionFeature = countryGeoJson.features.find(
        (f) => f.properties?.[regionNameProperty] === activeRegion,
      );
      loadForRegion(activeRegion, regionFeature);
    } else if (!activeRegion) {
      onFocusedPostcodeChange(null);
      resetPolygons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRegion, countryGeoJson, regionNameProperty, loadForRegion, resetPolygons]);

  const activePolygons = activeRegion ? polygonsByRegion[activeRegion] ?? null : null;
  const usePolygons = !!activePolygons;

  // Per-region URL presence — used by CountryInfoCard to show the badge
  // even before polygons finish loading.
  const hasPolygonsForActive = !!(
    activeRegion &&
    resolveUrl(
      activeRegion,
      countryGeoJson?.features.find((f) => f.properties?.[regionNameProperty] === activeRegion),
    )
  );

  useUnifiedCountryOutline({ mapRef, ready, activeCountry });
  useUnifiedRegionLayers({
    mapRef,
    ready,
    geojson: countryGeoJson,
    regionNameProperty,
    aggregated,
    cfg,
    activeRegion,
    hasPolygons: usePolygons,
  });
  useUnifiedPostcodeLayers({
    mapRef,
    ready,
    activeCountry,
    allMarkers,
    activeRegion,
    cfg,
    polygons: activePolygons,
    postcodeProperty,
    focusedPostcode,
  });

  // ── 6. Drill-in / drill-out crossfade + camera (steps 3–4) ─────────────
  useUnifiedDrillIn({
    mapRef,
    ready,
    activeCountry,
    geojson: countryGeoJson,
    aggregated,
    aggregatedCountry,
    regionNameProperty,
    incidentsCount: countryIncidents.length,
  });

  useUnifiedDrillOut({ mapRef, ready, activeCountry });

  // ── 7. In-country navigation (step 5) — region/postcode zoom transitions
  useUnifiedNavigation({
    mapRef,
    ready,
    activeCountry,
    activeRegion,
    focusedPostcode,
    geojson: countryGeoJson,
    regionNameProperty,
    postcodeProperty,
    activePolygons,
    allMarkers,
    aggregated,
    hasPolygonsForActive,
  });

  // ── 8. Region / postcode hover + click + tooltip (step 5) ──────────────
  // Reuse the legacy useMapInteractions: it operates on the shared layer ids
  // (regions, postcodes, postcode-polygons) and uses regionNameProperty —
  // both still present on our features (we additionally inject __regionName
  // for layer expressions, but the raw property survives the spread).
  const onPostcodeClick = useCallback(
    (pc: string) => {
      onFocusedPostcodeChange(pc);
    },
    [onFocusedPostcodeChange],
  );

  const { tooltip } = useMapInteractions({
    mapRef,
    ready,
    geojson: countryGeoJson,
    activeRegion,
    setActiveRegion: onActiveRegionChange,
    allMarkers,
    regionNameProperty,
    onPostcodeClick,
  });

  // Attach aggregated data to the region tooltip so the card can show totals.
  const liveTooltip =
    tooltip?.kind === 'region' ? { ...tooltip, data: aggregated[tooltip.name] ?? null } : tooltip;

  // ── 9a. Country loader anchor — visible between the click and the moment
  //         drill-in actually starts (geojson + GeoNames fetch). Anchored on
  //         COUNTRY_CENTERS so it sits over the right country in the world
  //         viewport. Reuses the projection-on-move pattern from the legacy
  //         CountryView country loader.
  const countryLoading = !!activeCountry && !!countryConfig && !countryGeoJson && !countryDataError;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !countryLoading || !activeCountry) {
      setCountryLoaderPos(null);
      return;
    }
    const center = COUNTRY_CENTERS[activeCountry];
    if (!center) return;

    const update = () => {
      const p = map.project(center);
      setCountryLoaderPos([p.x, p.y]);
    };
    update();
    map.on('move', update);
    return () => {
      map.off('move', update);
    };
  }, [mapRef, countryLoading, activeCountry]);

  // ── 9. Region loader anchor — project the active region's centroid to
  //       screen pixels on every camera move while polygons are loading.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !activeRegion || !polygonsLoading || !countryGeoJson) {
      setRegionLoaderPos(null);
      return;
    }
    const regionFeature = countryGeoJson.features.find(
      (f) => f.properties?.[regionNameProperty] === activeRegion,
    );
    if (!regionFeature) return;
    const center = featureBboxCenter(regionFeature);
    if (!center) return;

    const update = () => {
      const p = map.project(center);
      setRegionLoaderPos([p.x, p.y]);
    };
    update();
    map.on('move', update);
    return () => {
      map.off('move', update);
    };
  }, [mapRef, activeRegion, polygonsLoading, countryGeoJson, regionNameProperty]);

  // ── Imperative handle (legacy WorldView + CountryView handles merged) ──
  // fitCountry mirrors the legacy WorldView.fitCountry: bbox-from-geometry +
  // fitBounds with padding/maxZoom 6. Walks the cached world geojson, picks
  // the feature whose ISO3 maps to the requested alpha-2.
  useImperativeHandle(
    ref,
    () => ({
      getViewport: () => {
        const map = mapRef.current;
        if (!map) return null;
        const c = map.getCenter();
        return { center: [c.lng, c.lat], zoom: map.getZoom() };
      },
      fitCountry: (countryCode: string) => {
        const map = mapRef.current;
        const fc = worldGeoJsonRef.current;
        if (!map || !fc) return;

        const feature = fc.features.find((f) => {
          const iso3 = String(f.properties?.[ISO3_PROP] ?? '');
          return ISO3_TO_ISO2[iso3] === countryCode;
        });
        if (!feature) return;

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        const visit = (coords: any) => {
          if (typeof coords[0] === 'number') {
            const [x, y] = coords as [number, number];
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          } else {
            for (const c of coords) visit(c);
          }
        };
        visit((feature.geometry as any).coordinates);
        if (minX === Infinity) return;

        map.fitBounds(
          [
            [minX, minY],
            [maxX, maxY],
          ],
          { padding: 40, duration: 800, maxZoom: 6 },
        );
      },
    }),
    [mapRef],
  );

  // ── 10. Back / navigation handlers ─────────────────────────────────────
  const handleBack = useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      onBack({ center: [10, 25], zoom: 1.5 });
      return;
    }
    const c = map.getCenter();
    onBack({ center: [c.lng, c.lat], zoom: map.getZoom() });
  }, [mapRef, onBack]);

  const handleSidebarSelectCountry = useCallback(
    (code: string) => {
      const map = mapRef.current;
      if (map) {
        const c = map.getCenter();
        onSelectCountry(code, { center: [c.lng, c.lat], zoom: map.getZoom() });
      } else {
        onSelectCountry(code);
      }
    },
    [mapRef, onSelectCountry],
  );

  const flyToPostcode = useCallback(
    (marker: PostcodeMarker) => {
      onFocusedPostcodeChange(marker.postcode);
    },
    [onFocusedPostcodeChange],
  );

  // ── 11. Escape — step out one drill-down level (mirrors legacy) ────────
  useEffect(() => {
    if (!activeCountry) return; // world view — Escape has no role here

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = document.activeElement;
      const inField =
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        (el instanceof HTMLElement && el.isContentEditable);
      if (inField) return; // the focused field's own handler deals with it

      e.preventDefault();
      if (focusedPostcode) {
        onFocusedPostcodeChange(null);
      } else if (activeRegion) {
        onActiveRegionChange(null);
      } else {
        handleBack();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    activeCountry,
    focusedPostcode,
    activeRegion,
    onFocusedPostcodeChange,
    onActiveRegionChange,
    handleBack,
  ]);

  // ── Derived UI values ─────────────────────────────────────────────────
  const countryName = activeCountry ? COUNTRY_NAMES[activeCountry] ?? activeCountry : '';
  const totalIncidents = countryIncidents.reduce((a, i) => a + i.count, 0);
  const regionPostcodes = activeRegion
    ? (aggregated[activeRegion]?.postcodes ?? []).slice().sort((a, b) => b.count - a.count)
    : [];
  const regionTotal = activeRegion ? aggregated[activeRegion]?.total ?? 0 : 0;

  const worldSeverity = worldTooltip ? getSeverity(worldTooltip.severityCount, cfg) : null;

  return (
    <div className="relative w-full h-full flex">
      <div className="relative flex-1 min-w-0 z-1">
        <div ref={mapContainer} className="w-full h-full" />

        {/* Top-left navigation buttons — only shown inside a country */}
        {activeCountry && (
          <div className="absolute top-3 left-3 z-10 flex gap-2 flex-wrap">
            <Button
              theme={ButtonVariant.Secondary}
              onClick={handleBack}
              className="shadow-ui-kit-base"
            >
              ← World
            </Button>
            {activeRegion && (
              <Button
                theme={ButtonVariant.Primary}
                onClick={() => {
                  onFocusedPostcodeChange(null);
                  onActiveRegionChange(null);
                }}
                className="shadow-ui-kit-base"
              >
                {activeRegion} ✕
              </Button>
            )}
          </div>
        )}

        {/* Country info card — only when a region is selected (otherwise the
            RegionsSidebar carries the summary itself). */}
        {activeCountry && activeRegion && (
          <CountryInfoCard
            countryName={countryName}
            totalIncidents={totalIncidents}
            totalPostcodes={allMarkers.length}
            unmatchedCount={unmatchedCount}
            activeRegion={activeRegion}
            postcodeGeoJsonUrl={postcodeGeoJsonUrl}
            usePolygons={usePolygons}
            polygonsLoading={polygonsLoading}
            polygonsError={polygonsError}
            hasPolygonsForActive={hasPolygonsForActive}
          />
        )}

        {/* Country geojson error overlay */}
        {activeCountry && countryDataError && (
          <div className="absolute inset-0 flex items-center justify-center bg-v2blueGray-100 p-6">
            <AlertContainer color="red" className="text-center max-w-md">
              <span className="text-[13px]">{countryDataError}</span>
            </AlertContainer>
          </div>
        )}

        {/* Country geojson load spinner — anchored to the country center
            (COUNTRY_CENTERS). Visible from click until drill-in fires, so
            slow-network users get immediate feedback instead of a frozen
            world view. */}
        {countryLoading && countryLoaderPos && (
          <RegionLoader
            x={countryLoaderPos[0]}
            y={countryLoaderPos[1]}
            label={`Loading ${countryName}…`}
          />
        )}

        {/* Region polygons load spinner — anchored to region centroid */}
        {activeCountry && activeRegion && polygonsLoading && regionLoaderPos && (
          <RegionLoader
            x={regionLoaderPos[0]}
            y={regionLoaderPos[1]}
            label={`Loading ${activeRegion}…`}
          />
        )}

        {/* World tooltip — only on world view */}
        {!activeCountry && worldTooltip && worldSeverity && (
          <div
            className="fixed rounded-lg px-3 py-2 text-[13px] pointer-events-none z-[9999] whitespace-nowrap shadow-popup"
            style={{
              left: worldTooltip.x + 14,
              top: worldTooltip.y - 10,
              background: C.textPrimary,
              color: C.bg,
            }}
          >
            <div className="font-semibold mb-0.5">{worldTooltip.countryName}</div>
            <div className="text-[11px] text-v2blueGray-500 mb-1">{worldTooltip.countryCode}</div>
            <div style={{ color: worldSeverity.color }}>
              {worldTooltip.count.toLocaleString()} incidents
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: worldSeverity.color }}>
              {worldSeverity.label}
            </div>
            <div className="text-[11px] text-v2blueGray-500 mt-1.5 italic">
              Click to drill down →
            </div>
          </div>
        )}

        {/* Country tooltips — postcode / region (with data) / region (no data) */}
        {activeCountry && liveTooltip?.kind === 'postcode' && (
          <PostcodeTooltipCard tooltip={liveTooltip} cfg={cfg} />
        )}
        {activeCountry && liveTooltip?.kind === 'region' && liveTooltip.data && (
          <RegionTooltipCard tooltip={{ ...liveTooltip, data: liveTooltip.data }} cfg={cfg} />
        )}
        {activeCountry && liveTooltip?.kind === 'region' && !liveTooltip.data && (
          <SimpleRegionTooltip name={liveTooltip.name} x={liveTooltip.x} y={liveTooltip.y} />
        )}
      </div>

      {/* Right sidebar — countries / regions / postcodes by drill level */}
      {!activeCountry ? (
        <CountriesSidebar
          countryTotals={countryTotals}
          countrySums={countrySums}
          cfg={cfg}
          onSelect={handleSidebarSelectCountry}
        />
      ) : activeRegion ? (
        <PostcodeSidebar
          region={activeRegion}
          total={regionTotal}
          postcodes={regionPostcodes}
          cfg={cfg}
          focusedPostcode={focusedPostcode}
          onSelect={flyToPostcode}
          onClose={() => {
            if (focusedPostcode) {
              onFocusedPostcodeChange(null);
            } else {
              onActiveRegionChange(null);
            }
          }}
        />
      ) : (
        <RegionsSidebar
          countryName={countryName}
          totalIncidents={totalIncidents}
          totalPostcodes={allMarkers.length}
          unmatchedCount={unmatchedCount}
          aggregated={aggregated}
          cfg={cfg}
          onSelect={(regionName) => onActiveRegionChange(regionName)}
          onClose={handleBack}
        />
      )}
    </div>
  );
});

export default UnifiedMap;
